import { useState, useEffect } from 'react';
import { useCompanyInfo } from '../lib/cms';

export default function FloatingCta() {
  const companyInfo = useCompanyInfo();
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const contactLinks = [
    {
      id: 'phone',
      label: '40 55 55 99',
      sublabel: 'Ring direkte',
      href: `tel:${companyInfo.phoneRaw}`,
      icon: 'fa-solid fa-phone',
      bgClass: 'bg-[#004FA4] hover:bg-[#183E7F] text-white',
      iconClass: 'bg-[#004FA4] text-white',
      ariaLabel: `Ring til Hovendals på ${companyInfo.phone}`,
      isExternal: false,
    },
    {
      id: 'email',
      label: 'simon@hovendals.dk',
      sublabel: 'Send en e-mail',
      href: `mailto:${companyInfo.email}`,
      icon: 'fa-solid fa-envelope',
      bgClass: 'bg-slate-900 hover:bg-slate-800 text-white',
      iconClass: 'bg-slate-800 text-[#C2CCDF]',
      ariaLabel: `Send e-mail til ${companyInfo.email}`,
      isExternal: false,
    },
    {
      id: 'facebook',
      label: 'Følg på Facebook',
      sublabel: 'Hovendals',
      href: companyInfo.facebook,
      icon: 'fa-brands fa-facebook-f',
      bgClass: 'bg-[#1877F2] hover:bg-[#166fe5] text-white',
      iconClass: 'bg-[#1877F2] text-white',
      ariaLabel: 'Besøg Hovendals på Facebook',
      isExternal: true,
    },
  ];

  return (
    <>
      {/* Desktop & Tablet Floating Action Buttons (Højre side, lodret centreret på skærmen) */}
      <div className="hidden sm:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col items-end gap-3 pointer-events-none">
        {/* 3 Floating knapper: Telefon, Mail, Facebook med fold-ud på hover */}
        {contactLinks.map((item) => (
          <a
            key={item.id}
            href={item.href}
            target={item.isExternal ? '_blank' : undefined}
            rel={item.isExternal ? 'noopener noreferrer' : undefined}
            aria-label={item.ariaLabel}
            className="pointer-events-auto group flex items-center flex-row-reverse bg-white/95 backdrop-blur-md text-slate-900 rounded-full p-0.5 shadow-lg border border-slate-200/90 hover:border-[#004FA4]/80 hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 focus:outline-hidden focus:ring-2 focus:ring-[#004FA4] cursor-pointer"
          >
            {/* Cirkulært ikon (altid synligt) */}
            <span
              className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl shadow-sm transition-transform duration-300 group-hover:scale-105 shrink-0 ${item.bgClass}`}
            >
              <i className={item.icon} aria-hidden="true" />
            </span>

            {/* Fold-ud tekstboks (udvider sig mod venstre ved hover) */}
            <div className="max-w-0 opacity-0 group-hover:max-w-xs group-hover:opacity-100 group-hover:px-3 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out flex flex-col text-right">
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 leading-tight">
                {item.sublabel}
              </span>
              <span className="text-xs font-bold text-slate-900 leading-snug">
                {item.label}
              </span>
            </div>
          </a>
        ))}
      </div>

      {/* Mobil Floating Sticky Bar (bund-bjælke med telefon, mail og Facebook) */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/90 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-2.5 flex items-center justify-around gap-2 safe-area-pb">
        <a
          href={`tel:${companyInfo.phoneRaw}`}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-1.5 px-2 rounded-xl text-slate-900 active:scale-95 transition-all text-center"
          aria-label={`Ring til ${companyInfo.phone}`}
        >
          <span className="w-8 h-8 rounded-full bg-[#004FA4] text-white flex items-center justify-center text-xs shadow-xs">
            <i className="fa-solid fa-phone" aria-hidden="true" />
          </span>
          <span className="text-[11px] font-bold">Ring op</span>
        </a>

        <a
          href={`mailto:${companyInfo.email}`}
          className="flex-1 flex flex-col items-center justify-center gap-1 py-1.5 px-2 rounded-xl text-slate-900 active:scale-95 transition-all text-center"
          aria-label={`Send e-mail til ${companyInfo.email}`}
        >
          <span className="w-8 h-8 rounded-full bg-[#143463] text-white flex items-center justify-center text-xs shadow-xs">
            <i className="fa-solid fa-envelope text-[#C2CCDF]" aria-hidden="true" />
          </span>
          <span className="text-[11px] font-bold">E-mail</span>
        </a>

        <a
          href={companyInfo.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex flex-col items-center justify-center gap-1 py-1.5 px-2 rounded-xl text-slate-900 active:scale-95 transition-all text-center"
          aria-label="Besøg Hovendals på Facebook"
        >
          <span className="w-8 h-8 rounded-full bg-[#1877F2] text-white flex items-center justify-center text-xs shadow-xs">
            <i className="fa-brands fa-facebook-f" aria-hidden="true" />
          </span>
          <span className="text-[11px] font-bold">Facebook</span>
        </a>
      </div>
    </>
  );
}
